import { LocalizedParagraph } from '@mews-commander/ui';
import { WithAccountId, WithBillId } from '@mews-commander/types';
import { Body, Checkbox, Container, Stack, Title } from '@optimus-web/core';
import { LocalizedText, useScope, useToggle } from '@mews/framework';
import { BillState } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCustomerOrCompanyFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { getBill, isBillVirtual } from '@mews-commander/legacy-entities/src/ducks/bills';
import { useCommanderSelector } from '@mews-commander/utils';
import { getOrderedOpenBillIdsByOwnerId } from '../../../selectors';
import { areAllBillItemsSelected } from '../../../ducks/billItems';
import { getSelectedItemsUniqueBillIds } from '../../../ducks/itemAssignment';
import { useDeleteBills } from '../../../hooks/useDeleteBills';
import { MoveItemsButton } from './MoveItemsButton';
import { BillItemItemAssignment } from './BillItemItemAssignment';

interface AccountBillProps extends WithAccountId, Partial<WithBillId> {
    isCompany: boolean;
    isCurrentAccount?: boolean;
}

export const AccountBillList = ({ accountId, billId, isCompany, isCurrentAccount }: AccountBillProps) => {
    const scope = useScope() ?? '';
    const fullName = useCommanderSelector((state) => getCustomerOrCompanyFullName(state, { id: accountId }));
    const billIds = useCommanderSelector((state) => getOrderedOpenBillIdsByOwnerId(state, { ownerId: accountId }));
    const enterpriseId = useCurrentEnterpriseId();

    const filteredBillIds = useCommanderSelector((state) =>
        billIds.filter((id) => id !== billId && !isBillVirtual(state, { id }))
    );

    const bill = useCommanderSelector((state) => (billId ? getBill(state, { id: billId }) : undefined));
    const isBillClosed = bill?.state === BillState.Closed;

    const MoveBillCurrentAccountEmptyMessage = isCompany
        ? TextKey.MoveBillCompanyEmptyMessage
        : TextKey.MoveBillCustomerEmptyMessage;
    const emptyMessage = billId && isCurrentAccount ? MoveBillCurrentAccountEmptyMessage : TextKey.MoveBillEmptyMessage;

    const itemBillIds = useCommanderSelector((state) => getSelectedItemsUniqueBillIds(state, scope));

    const billsWithAllItemsSelected = useCommanderSelector((state) =>
        itemBillIds.filter(
            (itemBillId) =>
                // @ts-expect-error
                areAllBillItemsSelected(state, { billId: itemBillId, scope }) &&
                !isBillVirtual(state, { id: itemBillId })
        )
    );

    const [selectedDeleteEmptyBills, toggleDeleteEmptyBills] = useToggle({
        initialValue: !!billsWithAllItemsSelected.length,
    });

    const { mutate } = useDeleteBills();

    const deleteEmptyBills = (targetBill?: string) => {
        const billsToDelete = billsWithAllItemsSelected.filter(
            (billAllItemsSelected) => billAllItemsSelected !== targetBill
        );

        if (selectedDeleteEmptyBills && billsToDelete.length) {
            mutate({
                billIds: billsToDelete,
                enterpriseId,
            });
        }
    };

    return (
        <Stack spacing="200" vertical>
            {isCurrentAccount ? <Title level={3}>{fullName}</Title> : null}
            <Stack vertical>
                <Body size="small">
                    <LocalizedText textKey={TextKey.MoveBillDeleteEmptyBillsCheckboxHeading} />
                </Body>
                <Checkbox
                    disabled={!billsWithAllItemsSelected.length}
                    checked={selectedDeleteEmptyBills}
                    onChange={toggleDeleteEmptyBills}
                >
                    <LocalizedText textKey={TextKey.MoveBillDeleteEmptyBillsCheckboxLabel} />
                </Checkbox>
            </Stack>
            {filteredBillIds?.length && !isBillClosed ? (
                <Stack vertical>
                    {filteredBillIds.map((id: string) => (
                        <BillItemItemAssignment
                            accountId={accountId}
                            key={id}
                            billId={id}
                            deleteEmptyBills={deleteEmptyBills}
                            isCurrentAccount={isCurrentAccount}
                        />
                    ))}
                </Stack>
            ) : null}
            {!filteredBillIds?.length ? <LocalizedParagraph variant="secondary" textKey={emptyMessage} /> : null}
            <Container padding={['50', '0']}>
                <Stack fill>
                    <MoveItemsButton scope={scope} accountId={accountId} deleteEmptyBills={deleteEmptyBills} />
                </Stack>
            </Container>
        </Stack>
    );
};
